import { NmLoader } from '../../components/NmLoader';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../styles/comparison.css';
import {
  IoAlertCircleOutline,
  IoPulseOutline,
  IoReloadOutline,
  IoSendOutline,
  IoSparklesOutline,
  IoSwapHorizontalOutline,
} from 'react-icons/io5';
import { DashboardAiGenerativeBlocks } from '../../components/ai/DashboardAiGenerativeBlocks';
import { NmButton } from '../../components/NmButton';
import { NmIsoDateField } from '../../components/NmIsoDateField';
import { NmSegmentControl } from '../../components/NmSegmentControl';
import {
  athleteComparisonAi,
  type AthleteComparisonAnalysis,
  type AthleteComparisonMetric,
  type AthleteComparisonOverview,
  type AthleteComparisonSubject,
  type AthleteItem,
  type DashboardAiHistoryMessage,
} from '../../services/api';
import { formatAthleteMetricValue } from '../../utils/comparison/formatAthleteMetric';

interface Props {
  athletes: AthleteItem[];
  defaultAthleteId?: number | null;
}

const RANGE_PRESETS = [7, 28, 90, 180, 365] as const;
type RangePreset = (typeof RANGE_PRESETS)[number];
const SHOW_AI_COMPARISON_COACH = false;

const PRIMARY_METRICS = [
  'readiness',
  'hrv_ms',
  'ctl',
  'atl',
  'tsb',
  'tss',
  'duration_minutes',
  'distance_km',
  'ftp_watts',
  'eftp_watts',
  'mftp_watts',
  'w_prime_kj',
  'tte_seconds',
  'critical_speed',
  'running_cadence',
  'critical_swim_speed',
  'stroke_rate',
];

function isoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function rangeFor(days: RangePreset): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - (days - 1));
  return { from: isoDate(from), to: isoDate(to) };
}

/** Keep sign + digits + Latin unit in one LTR run inside RTL tables. */
function LtrMetric({ value }: { value: string }) {
  return (
    <span className="cmp-athletes__num" dir="ltr">
      {value}
    </span>
  );
}

export function AthleteComparisonPanel({ athletes, defaultAthleteId = null }: Props) {
  const { t, i18n } = useTranslation();
  const initialA = athletes.some((athlete) => athlete.id === defaultAthleteId)
    ? Number(defaultAthleteId)
    : athletes[0]?.id ?? 0;
  const initialB = athletes.find((athlete) => athlete.id !== initialA)?.id ?? 0;
  const [athleteAId, setAthleteAId] = useState(initialA);
  const [athleteBId, setAthleteBId] = useState(initialB);
  const [preset, setPreset] = useState<RangePreset>(28);
  const [range, setRange] = useState(() => rangeFor(28));
  const [overview, setOverview] = useState<AthleteComparisonOverview | null>(null);
  const [analysis, setAnalysis] = useState<AthleteComparisonAnalysis | null>(null);
  const [history, setHistory] = useState<DashboardAiHistoryMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (athletes.length < 2) return;
    if (!athletes.some((athlete) => athlete.id === athleteAId)) {
      setAthleteAId(athletes[0].id);
    }
    if (!athletes.some((athlete) => athlete.id === athleteBId) || athleteAId === athleteBId) {
      setAthleteBId(athletes.find((athlete) => athlete.id !== athleteAId)?.id ?? 0);
    }
  }, [athletes, athleteAId, athleteBId]);

  const payload = useMemo(
    () => ({
      athlete_a_id: athleteAId,
      athlete_b_id: athleteBId,
      from: range.from,
      to: range.to,
    }),
    [athleteAId, athleteBId, range],
  );

  const loadOverview = useCallback(async () => {
    if (!athleteAId || !athleteBId || athleteAId === athleteBId) return;
    setLoading(true);
    setError('');
    setAnalysis(null);
    setHistory([]);
    try {
      const response = await athleteComparisonAi.overview(payload);
      setOverview(response.data.data);
    } catch {
      setOverview(null);
      setError(t('compare.athletes.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [athleteAId, athleteBId, payload, t]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  const choosePreset = (days: RangePreset) => {
    setPreset(days);
    setRange(rangeFor(days));
  };

  const swapAthletes = () => {
    setAthleteAId(athleteBId);
    setAthleteBId(athleteAId);
  };

  const ask = async (prompt?: string) => {
    const message = (prompt ?? question).trim();
    if (!message || asking) return;
    const nextHistory = [...history, { role: 'user' as const, content: message }];
    setHistory(nextHistory);
    setQuestion('');
    setAsking(true);
    setError('');
    try {
      const response = await athleteComparisonAi.analyze({
        ...payload,
        message,
        locale: (['en', 'fa', 'ar', 'tr'].includes(i18n.language)
          ? i18n.language
          : 'en') as 'en' | 'fa' | 'ar' | 'tr',
        history: history.slice(-6),
      });
      setAnalysis(response.data.data);
      setOverview(response.data.data);
      setHistory([...nextHistory, { role: 'assistant', content: response.data.data.answer }]);
    } catch {
      setError(t('compare.athletes.aiFailed'));
    } finally {
      setAsking(false);
    }
  };

  const metricRows = useMemo(() => {
    if (!overview) return [];
    return overview.metrics
      .filter((metric) => PRIMARY_METRICS.includes(metric.key))
      .sort((a, b) => PRIMARY_METRICS.indexOf(a.key) - PRIMARY_METRICS.indexOf(b.key));
  }, [overview]);

  if (athletes.length < 2) {
    return (
      <div className="cmp-athletes__empty">
        <IoAlertCircleOutline aria-hidden />
        <strong>{t('compare.athletes.needTwo')}</strong>
        <p>{t('compare.athletes.needTwoHint')}</p>
      </div>
    );
  }

  return (
    <section className="cmp-athletes">
      <div className="cmp-athletes__control-card">
        <div className="cmp-athletes__control-head">
          <div>
            <span>{t('compare.athletes.stepLabel')}</span>
            <h2>{t('compare.athletes.selectTitle')}</h2>
            <p>{t('compare.athletes.selectHint')}</p>
          </div>
          <NmButton
            variant="ghost"
            size="sm"
            icon={<IoReloadOutline aria-hidden />}
            onClick={() => void loadOverview()}
            disabled={loading}
          >
            {t('compare.athletes.refresh')}
          </NmButton>
        </div>

        <div className="cmp-athletes__selectors">
          <label className="cmp-athletes__selector cmp-athletes__selector--a">
            <span>{t('compare.athletes.athleteA')}</span>
            <select value={athleteAId} onChange={(event) => setAthleteAId(Number(event.target.value))}>
              {athletes.map((athlete) => (
                <option key={athlete.id} value={athlete.id} disabled={athlete.id === athleteBId}>
                  {athlete.full_name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="cmp-athletes__swap"
            onClick={swapAthletes}
            aria-label={t('compare.athletes.swap')}
          >
            <IoSwapHorizontalOutline aria-hidden />
          </button>
          <label className="cmp-athletes__selector cmp-athletes__selector--b">
            <span>{t('compare.athletes.athleteB')}</span>
            <select value={athleteBId} onChange={(event) => setAthleteBId(Number(event.target.value))}>
              {athletes.map((athlete) => (
                <option key={athlete.id} value={athlete.id} disabled={athlete.id === athleteAId}>
                  {athlete.full_name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="cmp-athletes__range-row">
          <span>{t('compare.athletes.range')}</span>
          <NmSegmentControl
            className="cmp-athletes__range-pills"
            value={preset}
            options={RANGE_PRESETS.map((days) => ({
              value: days,
              label: t('compare.athletes.days', { count: days }),
            }))}
            onChange={choosePreset}
            variant="pills"
          />
          <div className="cmp-athletes__dates">
            <NmIsoDateField
              className="cmp-athletes__date-input"
              value={range.from}
              max={range.to}
              onChange={(from) => setRange((current) => ({ ...current, from }))}
              ariaLabel={t('compare.athletes.from')}
            />
            <span>—</span>
            <NmIsoDateField
              className="cmp-athletes__date-input"
              value={range.to}
              min={range.from}
              onChange={(to) => setRange((current) => ({ ...current, to }))}
              ariaLabel={t('compare.athletes.to')}
            />
          </div>
        </div>
      </div>

      {error && <div className="cmp-athletes__error">{error}</div>}

      {loading ? (
        <div className="cmp-athletes__loading">
          <NmLoader label={t('compare.athletes.loading')} />
          <small>{t('compare.athletes.loadingHint')}</small>
        </div>
      ) : overview ? (
        <>
          {!overview.same_discipline && (
            <div className="cmp-athletes__discipline-note">
              <IoAlertCircleOutline aria-hidden />
              <span>
                <strong>{t('compare.athletes.differentSport')}</strong>
                {t('compare.athletes.differentSportHint')}
              </span>
            </div>
          )}

          <div className="cmp-athletes__summary-grid">
            <SubjectCard subject={overview.athletes[0]} slot="a" locale={i18n.language} />
            <SubjectCard subject={overview.athletes[1]} slot="b" locale={i18n.language} />
          </div>

          <div className={`cmp-athletes__workspace${SHOW_AI_COMPARISON_COACH ? '' : ' cmp-athletes__workspace--single'}`}>
            <section className="cmp-athletes__metrics-card">
              <header>
                <div>
                  <span>{t('compare.athletes.evidenceLabel')}</span>
                  <h3>{t('compare.athletes.metricsTitle')}</h3>
                </div>
                <small>{t('compare.athletes.confidence', { value: Math.round(overview.confidence * 100) })}</small>
              </header>
              <div className="cmp-athletes__table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('compare.athletes.metric')}</th>
                      <th>{overview.athletes[0].name}</th>
                      <th>{overview.athletes[1].name}</th>
                      <th>{t('compare.athletes.delta')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metricRows.map((metric) => (
                      <MetricRow
                        key={metric.key}
                        metric={metric}
                        locale={i18n.language}
                        label={t(`compare.athletes.metricLabels.${metric.label_key}`, {
                          defaultValue: metric.label_key.replaceAll('_', ' '),
                        })}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="cmp-athletes__metrics-note">{t('compare.athletes.metricsNote')}</p>
            </section>

            {SHOW_AI_COMPARISON_COACH && <section className="cmp-athletes__ai-card">
              <header>
                <span className="cmp-athletes__ai-icon"><IoSparklesOutline aria-hidden /></span>
                <div>
                  <span>{t('compare.athletes.aiLabel')}</span>
                  <h3>{t('compare.athletes.aiTitle')}</h3>
                  <p>{t('compare.athletes.aiHint')}</p>
                </div>
                <small>{t('compare.athletes.aiReadOnly')}</small>
              </header>

              {!analysis && (
                <div className="cmp-athletes__insight-list">
                  {overview.insights.map((insight, index) => (
                    <div key={`${insight.code}-${index}`}>
                      <IoPulseOutline aria-hidden />
                      <span>{t(`compare.athletes.insights.${insight.code}`, {
                        delta: insight.delta,
                        defaultValue: insight.code.replaceAll('_', ' '),
                      })}</span>
                    </div>
                  ))}
                </div>
              )}

              {analysis && (
                <div className="cmp-athletes__ai-answer" aria-live="polite">
                  <p>{analysis.answer}</p>
                  <DashboardAiGenerativeBlocks
                    blocks={analysis.blocks ?? []}
                    onAction={() => undefined}
                  />
                </div>
              )}

              <div className="cmp-athletes__prompt-row">
                {overview.suggested_prompts.map((prompt) => (
                  <button type="button" key={prompt} onClick={() => void ask(t(`compare.athletes.prompts.${prompt}`))}>
                    {t(`compare.athletes.prompts.${prompt}`)}
                  </button>
                ))}
              </div>

              <div className="cmp-athletes__composer">
                <textarea
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void ask();
                    }
                  }}
                  placeholder={t('compare.athletes.questionPlaceholder')}
                  rows={2}
                />
                <button
                  type="button"
                  onClick={() => void ask()}
                  disabled={!question.trim() || asking}
                  aria-label={t('compare.athletes.ask')}
                >
                  {asking ? <NmLoader size="xs" inline decorative /> : <IoSendOutline aria-hidden />}
                </button>
              </div>
              <p className="cmp-athletes__disclaimer">{t('compare.athletes.disclaimer')}</p>
            </section>}
          </div>
        </>
      ) : null}
    </section>
  );
}

function SubjectCard({
  subject,
  slot,
  locale,
}: {
  subject: AthleteComparisonSubject;
  slot: 'a' | 'b';
  locale: string;
}) {
  const { t } = useTranslation();
  const metrics = [
    ['readiness', subject.evidence.readiness, '%'],
    ['ctl', subject.evidence.ctl, ''],
    ['tsb', subject.evidence.tsb, ''],
    ['training_stress', Number(subject.period.tss ?? 0), 'TSS'],
  ] as const;

  return (
    <article className={`cmp-athletes__subject cmp-athletes__subject--${slot}`}>
      <header>
        <span>{slot.toUpperCase()}</span>
        <div>
          <strong>{subject.name}</strong>
          <small>
            {t(`compare.athletes.disciplines.${subject.sport.discipline}`, {
              defaultValue: subject.sport.name ?? subject.sport.discipline,
            })}
          </small>
        </div>
        <i>{t('compare.athletes.dataConfidence', { value: Math.round(subject.confidence * 100) })}</i>
      </header>
      <div style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))` }}>
        {metrics.map(([key, value, unit]) => (
          <span key={key}>
            <small>{t(`compare.athletes.metricLabels.${key}`)}</small>
            <strong>
              <LtrMetric value={formatAthleteMetricValue(value, unit, locale)} />
            </strong>
          </span>
        ))}
      </div>
    </article>
  );
}

function MetricRow({
  metric,
  locale,
  label,
}: {
  metric: AthleteComparisonMetric;
  locale: string;
  label: string;
}) {
  return (
    <tr>
      <th>{label}</th>
      <td>
        <LtrMetric value={formatAthleteMetricValue(metric.athlete_a, metric.unit, locale)} />
      </td>
      <td>
        <LtrMetric value={formatAthleteMetricValue(metric.athlete_b, metric.unit, locale)} />
      </td>
      <td className={metric.delta == null ? '' : metric.delta > 0 ? 'is-positive' : metric.delta < 0 ? 'is-negative' : ''}>
        <LtrMetric value={formatAthleteMetricValue(metric.delta, metric.unit, locale, { signed: true })} />
      </td>
    </tr>
  );
}
